import * as Sentry from '@sentry/react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { queryKeys, useGetOrganizationQuery } from '@vizit/api';
import { useAuth } from '@vizit/auth';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import * as React from 'react';
import { OrganizationDetailsPage } from '../../../pages';
import { PagesProvider } from '../../../pages/providers/pages-provider';
import { beforeLoadVizitUserValidation } from '../../../router/route-utils';
import { BreadcrumbsPageTemplate } from '../../../templates';

export const Route = createFileRoute('/_authenticated/organizations/$organizationId')({
  staticData: {
    pageName: 'Organization Details',
  },
  beforeLoad: ({ context, location }) => beforeLoadVizitUserValidation(context, location),
  loader: async ({ params, context }) => {
    let organizationName = 'Organization Details';

    try {
      const organizationData = await context.queryClient.ensureQueryData({
        queryKey: queryKeys.organizations.detail({
          org_id: params.organizationId,
        }).queryKey,
        queryFn: () =>
          useGetOrganizationQuery.fetcher({
            org_id: params.organizationId,
          })(),
      });

      organizationName = organizationData?.name ?? 'Organization Details';
    } catch (error) {
      Sentry.captureException(error, {
        level: 'warning',
        tags: {
          feature: SENTRY_FEATURE_TAGS.Navigation,
          stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Navigation].breadcrumbs,
        },
        extra: {
          page: 'organization details',
        },
      });
    }

    return {
      breadcrumbs: [{ label: 'Organizations', href: '/organizations' }, { label: organizationName }],
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { organizationId } = useParams({ from: Route.id });
  const { userId, onExchangeToken } = useAuth();

  const handleOnExchangeToken = React.useCallback(
    async (organizationId: string, organizationName: string, portfolioId: string) => {
      await onExchangeToken(organizationId, organizationName, portfolioId);

      window.location.href = '/';
    },
    [onExchangeToken],
  );

  return (
    <PagesProvider template={BreadcrumbsPageTemplate}>
      <OrganizationDetailsPage
        organizationId={organizationId}
        userId={userId ?? ''}
        onExchangeToken={handleOnExchangeToken}
      />
    </PagesProvider>
  );
}
